import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, startWith, switchMap } from 'rxjs/operators';
import { NexusComponent } from 'app/core/components/nexus/nexus.component';
import { NexusFormResultModel } from 'app/core/models/nexus-form-result.model';
import { MembershipService } from 'app/core/services/membership.service';
import { PaymentMethodService } from 'app/services/payment-method.service';
import { PaymentMethodModel } from 'app/models/payment-method.model';
import { RegionModel } from 'app/core/models/region.model';
import { CustomerRegulatoryModel } from 'app/models/customer-regulatory.model';
import { AkamaiService } from 'app/services/akamai.service';

@Component({
    selector: 'app-payment-method-bank',
    templateUrl: './payment-method-bank.component.html',
    styleUrls: ['./payment-method-bank.component.css'],
    standalone: false
})
export class PaymentMethodBankComponent extends NexusComponent implements OnInit, OnDestroy {
    public regions_model: Array<RegionModel>;
    public payment_methods_model: Array<PaymentMethodModel>;
    public payment_method_model: PaymentMethodModel = new PaymentMethodModel();
    public result_model: NexusFormResultModel = new NexusFormResultModel();
    public simple_validation_model: NexusFormResultModel = new NexusFormResultModel();
    public regulatory_model: CustomerRegulatoryModel;
    public un_link_pressed: boolean;
    public number_mask_placeholder = 'Account Number';
    public first_payment_method = true;

    public bank_name_query = '';
    public bank_name_options$: Observable<Array<string>> = of([]);
    public bank_name_loading = false;
    public routing_bank_name_loading = false;
    private last_auto_bank_name = '';
    private bank_name_user_edited = false;

    public form = new UntypedFormGroup({
        ch_name_on_check: new UntypedFormControl('', Validators.required),
        ch_number_mask: new UntypedFormControl('', Validators.required || Validators.minLength(5)),
        ch_transit_mask: new UntypedFormControl('', this.ValidateBankRouting),
        ch_bank_name: new UntypedFormControl('', Validators.required),
        default_account :  new UntypedFormControl(true)
    });

    public edit = true;

    constructor(private route: ActivatedRoute, private router: Router, private membership: MembershipService, private payment_method_service: PaymentMethodService,
        private akamaiService: AkamaiService) {
        super();
        this.un_link_pressed = false;

    }

    ngOnInit() {
        this.akamaiService.injectScript();
        this.route.data.subscribe(({ regions_model, payment_methods_model, payment_method_model, regulatory_model }) => {
            this.regions_model = regions_model;
            this.payment_methods_model = payment_methods_model;
            this.regulatory_model = regulatory_model;
            if (payment_method_model) {
                this.payment_method_model = payment_method_model;
                this.form.patchValue(this.payment_method_model);
                this.number_mask_placeholder = 'Account Number '+payment_method_model.ch_number_mask;
            }

            if(this.payment_methods_model.length > 0){
            this.first_payment_method = false;
          }
        });

        const bankNameControl = this.form.get('ch_bank_name') as UntypedFormControl;
        this.bank_name_options$ = bankNameControl.valueChanges.pipe(
            startWith(bankNameControl.value || ''),
            map((value: any) => (value || '').toString().trim()),
            map((query: string) => {
                if (query && query !== this.last_auto_bank_name) {
                    this.bank_name_user_edited = true;
                }
                this.bank_name_query = query;
                return query;
            }),
            debounceTime(250),
            distinctUntilChanged(),
            switchMap((query: string) => {
                if (!query) {
                    this.bank_name_loading = false;
                    this.bank_name_user_edited = false;
                    return of([]);
                }
                this.bank_name_loading = true;
                return this.payment_method_service.SearchBankNames(query).pipe(
                    finalize(() => {
                        this.bank_name_loading = false;
                    })
                );
            })
        );

        const routingControl = this.form.get('ch_transit_mask') as UntypedFormControl;
        routingControl.valueChanges.pipe(
            map((value: any) => (value || '').toString().replace(/\D/g, '')),
            debounceTime(250),
            distinctUntilChanged(),
            filter((routing: string) => routing.length === 9),
            switchMap((routing: string) => {
                this.routing_bank_name_loading = true;
                return this.payment_method_service.GetBankNameByRouting(routing).pipe(
                    catchError(() => of('')),
                    finalize(() => {
                        this.routing_bank_name_loading = false;
                    })
                );
            })
        ).subscribe((bankName: string) => {
            const current = (bankNameControl.value || '').toString().trim();
            if (!bankName) {
                const canAutoClear = !!current && (current === this.last_auto_bank_name || this.bank_name_user_edited === false);
                if (canAutoClear) {
                    this.last_auto_bank_name = '';
                    this.bank_name_user_edited = false;
                    bankNameControl.setValue('', { emitEvent: false });
                    this.bank_name_query = '';
                }
                return;
            }

            const canAutoFill = !current || this.bank_name_user_edited === false;
            if (canAutoFill) {
                this.last_auto_bank_name = bankName;
                this.bank_name_user_edited = false;
                bankNameControl.setValue(bankName, { emitEvent: false });
                this.bank_name_query = bankName;
            }
        });

        this.form.valueChanges.subscribe((form: any) => {
            this.PopulateModelFromForm(form, this.payment_method_model);

            if(!this.edit)
            {
                this.edit = true;
                this.form.get('ch_number_mask').setValue('');
                this.form.get('ch_transit_mask').setValue('');
                this.payment_method_model.ch_number_mask = null;
                this.payment_method_model.ch_transit_mask = null;
            }
        });
        this.form.statusChanges.subscribe(status => {
            this.checkSimpleValidations();
        });
    }

    ngOnDestroy(): void {
        this.akamaiService.removeScript();
    }

    onBlurPlaceholder(){
        this.checkSimpleValidations();
        this.number_mask_placeholder =  this.payment_method_model.ch_number_mask.indexOf('xxx') > -1 ? 'Account Number '+ this.payment_method_model.ch_number_mask : 'Account Number';
        return this.number_mask_placeholder ;
    }
    onFocusPlaceholder(){
        return this.number_mask_placeholder = 'Account Number';
    }
    onKeyDown(event:KeyboardEvent):void{
        const forbiddenKeys = ['e','E','+','-',' '];
        if(forbiddenKeys.includes(event.key)){
          event.preventDefault();
        }
      }

    checkSimpleValidations() {
        const errors = [];

        if (this.form.get('ch_number_mask').invalid === true && this.form.get('ch_number_mask').value) {
            errors.push('Account Number must be numeric and at least 5 digits long.');
        }
        if (this.form.get('ch_transit_mask').invalid === true && this.form.get('ch_transit_mask').value) {
            errors.push('Routing Number must be numeric and at least 9 digits long.');
        }

        if (errors.length > 0) {
            this.simple_validation_model.Error( errors.join('\n\n') );
        }
        else {
            this.simple_validation_model.Reset();
        }
    }

    submit() {
        this.result_model.Reset();
        this.PopulateModelFromForm(this.form.getRawValue(), this.payment_method_model);
        this.StartSpinner();
        this.payment_method_model.account_type = 'Checking';
        this.payment_method_service.SavePaymentMethod(this.payment_method_model).subscribe(
            data => {
                this.membership.OverrideGoodStanding();
                this.router.navigate(['/payment-methods']);
                this.StopSpinner();
            },
            err => {
                this.result_model.Error(err);
                this.StopSpinner();
            });
    }

  public onPrimaryPaymentMethodSelected(primary: PaymentMethodModel) {
    this.payment_methods_model.forEach(pm => pm.default_account = pm.id === primary.id);
    this.payment_method_model.default_account = this.payment_method_model.id === primary.id;
    this.form.get('default_account').setValue(this.payment_method_model.default_account);
  }


}
